import React from 'react';
import { InputNumber } from 'antd';
import styled from 'styled-components';
import type { InputNumberProps } from 'antd';

const InputWrapper = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 40px;
`;

const StyledInput = styled(InputNumber)`
  &.ant-input-number {
    width: 60px;
    border-radius: 3px 0 0 3px;
    border-right: none;
    
    .ant-input-number-input {
      height: 40px;
      text-align: center;
      font-size: 14px;
      padding: 4px 11px;
      color: #000;
    }

    &:hover, &:focus {
      border-color: #d9d9d9;
      box-shadow: none;
    }
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #1B3160;
  width: 24px;
  border-radius: 0;
`;

const ControlButton = styled.button`
  height: 20px;
  width: 100%;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 12px;

  &:first-child {
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    color: rgba(255, 255, 255, 0.3);
    background: transparent;
  }
`;

const UnitLabel = styled.div`
  background: #1B3160;
  color: white;
  padding: 0 8px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: 0 3px 3px 0;
`;

interface StyledNumberInputProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  disabled?: boolean;
  size?: 'large' | 'middle' | 'small';
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  unit?: string;
  status?: string;
  allowDecimals?: boolean;
  onFocus?: (value: number | null) => void;
}

const StyledNumberInput: React.FC<StyledNumberInputProps> = ({
  value,
  onChange,
  min = 0,
  placeholder = '00',
  disabled = false,
  size = 'middle',
  onBlur,
  unit,
  status,
  allowDecimals = false,
  onFocus
}) => {

  const handleIncrement = () => {
    const currentValue = value || 0;
    const newValue = currentValue + (1);
    onChange?.(newValue);
  };

  const handleDecrement = () => {
    const currentValue = value || 0;
    if (currentValue > 0) {
      const newValue = Math.max(0, currentValue - (1));
      onChange?.(newValue);
    }
  };

  const isAtMin = (val: number | null | undefined): boolean => {
    if (val === null || val === undefined) return true;
    return val <= 0;
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numValue = parseFloat(inputValue);

    if (!isNaN(numValue)) {
      if (allowDecimals) {
        onChange?.(Number(numValue.toFixed(2)));
      } else {
        onChange?.(numValue);
      }
    } else {
      onChange?.(0);
    }

    onBlur?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement;
      const value = parseFloat(target.value);

      if (!isNaN(value)) {
        if (allowDecimals) {
          onChange?.(Number(value.toFixed(2)));
        } else {
          onChange?.(value);
        }
      }
      target.blur();
    }
  };

  return (
    <InputWrapper>
      <StyledInput
        value={value}
        min={0}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        controls={false}
        precision={allowDecimals ? 2 : 0}
        step={allowDecimals ? 0.01 : 1}
        onFocus={(e) => onFocus?.(Number(e.target.value))}
      />
      {/* <ControlsWrapper>
        <ControlButton
          type="button"
          onClick={handleIncrement}
          disabled={disabled}
        >
          ∧
        </ControlButton>
        <ControlButton
          type="button"
          onClick={handleDecrement}
          disabled={disabled || isAtMin(value)}
        >
          ∨
        </ControlButton>
      </ControlsWrapper> */}
      {unit && <UnitLabel>{unit}</UnitLabel>}
    </InputWrapper>
  );
};

export default StyledNumberInput; 